import React from "react";
import { Source, Layer, Marker, MarkerDragEvent } from "react-map-gl";
import PlaceIcon from "@mui/icons-material/Place";
import { Bedtime, LunchDining } from "@mui/icons-material";
import { getLineString } from "./ThumbnailMapUtils";
import { PRETTY_BLUE } from "../trips/form/BorderWithLabel";
import { updateWaypoint, useRiskViewerState, WaypointExpanded } from "../../features/riskViewer/riskViewerSlice";
import { Waypoint } from "../../generated/gpb/api/route";
import { useDispatch } from "react-redux";
import { selectWaypointFromMap } from "../location/LocationSelectModal";

/**
 * A component that renders selection waypoints and lines on a map.
 * Uses data from the risk viewer state to render waypoints and a connecting line.
 */
const SelectionWaypoints: React.FC = () =>
{
    const waypoints = useRiskViewerState().waypoints ?? [];

    type ValidityType = { validWaypoints: Array<WaypointExpanded>, originalIndicies: Array<number>, isContiguous: boolean };
    function getValidWaypoint( waypoint: WaypointExpanded | undefined ): waypoint is WaypointExpanded
    {
        return ( waypoint !== undefined &&
            waypoint.location?.location?.latitude !== undefined &&
            waypoint.location?.location?.longitude !== undefined &&
            waypoint.location?.location?.latitude !== 0 &&
            waypoint.location?.location?.longitude !== 0 );
    }
    /**
     * Filters and returns valid waypoints with defined location data.
     * @returns {Waypoint[]} - The array of valid waypoints.
     * @param waypointsToValidate
     */
    const getValidWaypoints = ( waypointsToValidate: Array<WaypointExpanded | undefined> ): ValidityType =>
    {
        const indicies: Array<number> = [];
        const validWaypoints: Array<WaypointExpanded> = waypointsToValidate.filter( ( waypoint, index ): waypoint is WaypointExpanded =>
        {
            const isValid = getValidWaypoint( waypoint );
            if ( isValid ) indicies.push( index );
            return isValid;
        } );
        let isContiguous = true;
        let prior: number | undefined = undefined;
        indicies.forEach( ( each ) =>
        {
            if ( prior !== undefined )
            {
                if ( each - prior !== 1 )
                {
                    isContiguous = false;
                }
            }
            prior = each;
        } );
        return { validWaypoints: validWaypoints, originalIndicies: indicies, isContiguous };
    };
    const renderIcon = ( waypoint: Waypoint | WaypointExpanded, index: number ) =>
    {
        if ( index === 0 || index === validWaypoints.length - 1 )
        {
            return <PlaceIcon sx = {{ alignContent: "center", paddingTop: "5px", height: "30px", width: "30px" }}/>;
        }
        if ( waypoint.overnightStop !== undefined )
        {
            return <Bedtime sx = {{ alignContent: "center", paddingTop: "5px", height: "30px", width: "30px" }}/>;
        }
        if ( waypoint.pitStop !== undefined )
        {
            return <LunchDining sx = {{ alignContent: "center", paddingTop: "5px", height: "30px", width: "30px" }}/>;
        }
        return null;
    };
    const dispatch = useDispatch();
    const validityResult = waypoints.length > 0 ? getValidWaypoints( waypoints )
        : { validWaypoints: [], originalIndicies: [], isContiguous: false };
    const validWaypoints = validityResult.validWaypoints;
    const lineData = ( validityResult.isContiguous || validWaypoints.length === waypoints.length ) ? getLineString( validWaypoints ) : null;
    function onDragEnd( event: MarkerDragEvent, index: number )
    {
        selectWaypointFromMap( event.lngLat, dispatch, index );
    }
    function onDrag( event: MarkerDragEvent, index: number, waypoint: WaypointExpanded )
    {
        const lngLat = event.lngLat;
        const waypointUpdated: WaypointExpanded = {
            ...waypoint,
            location:
                {
                    location: {
                        latitude: lngLat.lat,
                        longitude: lngLat.lng
                    },
                    locationName: "", // clear place name till drag is complete
                    valid: false
                }
        };
        dispatch( updateWaypoint( { index: index, waypoint: waypointUpdated, isDrag: true } ) );
    }
    return (
        <React.Fragment>
            {lineData && (
                <Source id = "lineLayer" type = "geojson" data = {lineData}>
                    <Layer
                        id = "lineLayer"
                        type = "line"
                        paint = {{
                            "line-color": PRETTY_BLUE,
                            "line-width": 3,
                            "line-dasharray": [ 2, 1 ], // Dotted line style
                        }}
                    />
                </Source>
            )}
            {validWaypoints.map( ( waypoint, index ) =>
            {
                return ( waypoint.location?.location !== undefined ? (
                    <Marker
                        key = {`Input-Waypoint-${index}`}
                        longitude = {waypoint.location.location.longitude}
                        latitude = {waypoint.location.location.latitude}
                        draggable
                        onDragEnd = {( event ) => onDragEnd( event, validityResult.originalIndicies[index] )}
                        onDrag = {( event ) => onDrag( event, validityResult.originalIndicies[index], waypoint )}
                    >
                        {renderIcon( waypoint, index )}
                    </Marker>
                ) : null );
            } ) }
        </React.Fragment>
    );
};

export default SelectionWaypoints;
